import type * as AgGridGlobals from "ag-grid-community";

import { DashboardWindow } from "../dashboardHelpers";

import {
  AGGridThemeConfig,
  ChartJSThemeConfig,
  DEFAULT_SEMANTIC_TOKENS,
  TailwindThemeConfig,
  Theme,
  ThemeCleanupFn,
  ThemeSetupFn,
} from "./types";

// Stores the cleanup function from the current theme's setup (if any)
let currentThemeCleanup: ThemeCleanupFn | null = null;

const TAILWIND_THEME_VARIABLES = {
  // General
  "--font-themed": { fallback: "Inter, system-ui, -apple-system, sans-serif" },
  "--radius-themed": { fallback: "0.5rem" },
  // Main surface
  "--color-main-background": { fallback: "#ffffff" },
  "--color-main-background-secondary": { fallback: "#f5f5f5" },
  "--color-main-background-tertiary": { fallback: "#ebebeb" },
  "--color-main-chip-background": { fallback: "#dee9fe" },
  "--color-main-foreground": { fallback: "#111827" },
  "--color-main-muted-foreground": { fallback: "#6b7280" },
  "--color-main-border": { fallback: "#e5e7eb" },
  "--shadow-main": { fallback: "none" },
  "--color-main-text-button-hover-background": { fallback: "#f3f4f6" },
  "--color-main-text-button-hover-foreground": { fallback: "#111827" },
  "--radius-main-text-button": { fallback: "var(--radius-themed)" },
  // Nav surface
  "--color-nav-background": { fallback: "#f9fafb" },
  "--color-nav-foreground": { fallback: "#111827" },
  "--color-nav-muted-foreground": { fallback: "#6b7280" },
  "--color-nav-border": { fallback: "#e5e7eb" },
  "--shadow-nav": { fallback: "none" },
  "--color-nav-text-button-hover-background": { fallback: "#f3f4f6" },
  "--color-nav-text-button-hover-foreground": { fallback: "#111827" },
  "--radius-nav-text-button": { fallback: "var(--radius-themed)" },
  // Card surface
  "--color-card-background": { fallback: "#ffffff" },
  "--color-card-foreground": { fallback: "#111827" },
  "--color-card-muted-foreground": { fallback: "#6b7280" },
  "--color-card-border": { fallback: "#e5e7eb" },
  "--color-card-text-button-hover-background": { fallback: "#f3f4f6" },
  "--color-card-text-button-hover-foreground": { fallback: "#111827" },
  "--radius-card-text-button": { fallback: "var(--radius-themed)" },
  "--shadow-card": { fallback: "0 1px 3px 0 rgb(0 0 0 / 0.05), 0 1px 2px -1px rgb(0 0 0 / 0.05)" },
  // AI Image block
  "--color-ai-image-background": { fallback: "#DEE9FE" },
  "--shadow-ai-image-inset": { fallback: "inset 0px 0px 22px 22px #DEE9FE" },
  // Text block
  "--color-text-block-foreground": { fallback: "#ffffff" },
  // Primary button
  "--color-primary-button-background": { fallback: "#4f46e5" },
  "--color-primary-button-foreground": { fallback: "#ffffff" },
  "--color-primary-button-hover-background": { fallback: "#4f46e5" },
  "--color-primary-button-hover-foreground": { fallback: "#ffffff" },
  "--shadow-primary-button": { fallback: "none" },
  "--color-primary-button-border": { fallback: "transparent" },
  // Secondary button
  "--color-secondary-button-background": { fallback: "#8b5cf6" },
  "--color-secondary-button-foreground": { fallback: "#ffffff" },
  "--color-secondary-button-hover-background": { fallback: "#8b5cf6" },
  "--color-secondary-button-hover-foreground": { fallback: "#ffffff" },
  "--shadow-secondary-button": { fallback: "none" },
  "--color-secondary-button-border": { fallback: "transparent" },
};

type TailwindThemeVariableName = keyof typeof TAILWIND_THEME_VARIABLES;

// Define CSS Tailwind theme variables so that Tailwind CDN JS can create shorthand custom
// utility classes like `bg-primary`, `text-nav-foreground`, `rounded-surface`, etc.
//
// Ref: https://tailwindcss.com/docs/theme#theme-variable-namespaces
//
// Note that the @theme <style> block must statically declare all custom theme variables in
// order for Tailwind CDN JS to generate the custom utility classes. To allow for dynamic
// overriding the values of these variables, we set the Tailwind theme variables to
// var(--theme--<variable-name>) to add a layer of redirection, e.g.
//
//   @theme {
//     --color-primary: var(--theme--color-primary, <fallback value>);
//     --color-secondary: var(--theme--color-secondary, <fallback value>);
//     ...
//   }
//
export const TAILWIND_STYLE_TAG = `<style id="tailwindStyleTag" type="text/tailwindcss">
  @theme {
    ${Object.entries(TAILWIND_THEME_VARIABLES)
      .map(([name, { fallback }]) => `${name}: var(--theme${name}, ${fallback});`)
      .join("\n")}
    /* The iframe root pins its own font-size to 0.875rem (14px) via an inline style, so all
       rem-based text utilities render at 87.5% of nominal. text-xs and text-sm are pinned to
       explicit px so they don't ride that scale (they'd otherwise render at 10.5px / 12.25px). */
    --text-xs: 11px;
    --text-sm: 13px;
  }
  @utility bg-page {
    background: var(--theme--page-background, #F9FAFB);
  }
  @utility bg-dashboard-container {
    background: var(--theme--dashboard-container-background, transparent);
  }
  @utility rounded-dashboard-container {
    border-radius: var(--theme--dashboard-container-border-radius, 0);
  }
  @utility shadow-dashboard-container {
    box-shadow: var(--theme--dashboard-container-shadow, none);
  }
  @utility border-dashboard-container {
    border-color: var(--theme--dashboard-container-border-color, transparent);
  }
  @utility borderw-main {
    border-width: var(--theme--main-border-width, 0px);
  }
  @utility borderw-nav {
    border-width: var(--theme--nav-border-width, 0px);
  }
  @utility borderw-card {
    border-width: var(--theme--card-border-width, 0px);
  }
  @utility glow-none {
    box-shadow: 0 0 0px transparent;
  }
  @utility glow-primary {
    box-shadow: 0 0 8px color-mix(in srgb,var(--color-primary-button-background),transparent 30%);
  }
  @utility themed-menu-scrollbar {
    overscroll-behavior: contain;

    /* Use standard scrollbar-* CSS props as fallback for non-Chromium browsers */
    @supports not selector(::-webkit-scrollbar) {
      scrollbar-width: thin;
      scrollbar-color: color-mix(in srgb, var(--color-main-muted-foreground) 25%, transparent) transparent;
      /* Container cannot be rounded - otherwise, scrollbar would stick out through the corners */
      @apply !rounded-none;
    }

    &::-webkit-scrollbar {
      @apply w-2;
    }
    &::-webkit-scrollbar-track {
      @apply bg-transparent;
    }
    &::-webkit-scrollbar-thumb {
      @apply bg-main-muted-foreground/25;
      @apply border-0 border-transparent;
      /* Inset scrollbar thumb to leave space for rounded corners */
      border-width: calc(var(--radius-themed) / 2) 2px calc(var(--radius-themed) / 2) 2px;
      background-clip: padding-box;
    }
  }
  @utility themed-text-block-card {
    background: var(--theme--text-block-background, linear-gradient(116.26deg, #153AF0 43.88%, #528FFF 100%));
    color: var(--theme--color-text-block-foreground, #ffffff);
    border: var(--theme--text-block-border, none);
  }
  @layer components {
    :root {
      --data-table-search-icon-width: 18px;
      --data-table-search-width: 10rem;
      --data-table-actions-offset: 160px;
    }
    .themed-dashboard-card {
      @apply p-3 sm:p-4 md:p-6 h-full flex flex-col rounded-themed
        text-card-foreground shadow-card transition-all;
      /* Spec-built themes paint a translucent gradient (page bleeds through the light corner);
         legacy themes fall back to the solid card color. */
      background: var(--theme--card-surface-background, var(--color-card-background));
    }
    .themed-card-title {
      @apply text-lg font-semibold mb-4 flex-shrink-0;
    }
    .themed-nav-button {
      @apply flex items-center gap-1 px-2 md:px-4 py-1 md:py-2
        text-xs xl:text-sm transition-colors cursor-pointer
        text-nav-foreground
        hover:bg-nav-text-button-hover-background hover:text-nav-text-button-hover-foreground
        focus:outline-primary-button-background
        focus-visible:outline-primary-button-background
        disabled:opacity-50 disabled:cursor-not-allowed;
      border-radius: var(--radius-nav-text-button);
    }
    .themed-slicer-input {
      @apply inline-flex items-center gap-2 px-4 pr-11 py-2
        transition-colors cursor-pointer field-sizing-content
        text-sm font-semibold min-w-25 appearance-none
        text-card-foreground bg-card-background rounded-themed
        border-card-border borderw-card shadow-card
        hover:bg-card-text-button-hover-background hover:text-card-text-button-hover-foreground
        focus:outline-none focus:ring-2 focus:ring-primary-button-background
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-button-background
        caret-primary-button-background;
    }
    .themed-data-table-search-wrapper {
      @apply relative flex items-center;
    }
    .themed-data-table-search-icon {
      @apply absolute left-3 text-card-foreground/50 pointer-events-none
        !text-[length:var(--data-table-search-icon-width)]
        transition-[left] duration-200;
    }
    .themed-data-table-search-wrapper:has(.themed-data-table-search-collapsed) .themed-data-table-search-icon {
      left: calc(var(--data-table-search-icon-width) / 2);
    }
    .themed-data-table-search {
      @apply pl-10 pr-10 py-2.5
        text-sm appearance-none border-0
        text-card-foreground bg-card-foreground/5 rounded-lg
        hover:bg-card-foreground/10
        focus:outline-none focus:bg-card-foreground/10
        caret-card-foreground
        transition-all duration-200
        w-[var(--data-table-search-width)];
    }
    .themed-data-table-search::placeholder {
      @apply text-card-foreground/50;
    }
    .themed-data-table-search-collapsed {
      @apply pl-2.5 pr-2.5 bg-transparent cursor-pointer
        w-[calc(2*var(--data-table-search-icon-width))];
    }
    .themed-data-table-search-collapsed::placeholder {
      @apply opacity-0;
    }
    .themed-data-table-search-clear {
      @apply absolute right-3 text-card-foreground/50 cursor-pointer
        hover:text-card-foreground transition-colors !text-[18px];
    }
    .themed-data-table-action-button {
      @apply w-8 h-8 rounded-full
        flex items-center justify-center cursor-pointer
        bg-card-foreground/5 text-card-foreground/50
        hover:bg-card-foreground/10 hover:text-card-foreground/70
        active:scale-[0.97] active:bg-card-foreground/10
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-button-background;
    }
    .themed-data-table-action-button .material-icons {
      @apply !text-[length:var(--data-table-search-icon-width)];
    }
    /* Data tables on mobile (<640px) — BOTH the drill-down modal (Figma 5743-6504) and in-dashboard
       table blocks get the same search treatment: the search is PRE-EXPANDED to a full-width bar
       sitting in the flow below the title, magnifier anchored left, rather than the desktop
       collapsed icon lifted up onto the title line.

       Desktop positions the wrapper with inline styles from createDataTable
       (position:absolute; top:-44px; right:var(--data-table-actions-offset)) to clear the action
       buttons, so !important is needed to beat them; !relative (not !static) keeps the
       absolutely-positioned magnifier anchored to the wrapper. !left-3 beats the
       higher-specificity :has(collapsed) icon rule above. Styling the collapsed class identically
       to the expanded one is what makes it "pre-expanded" — focus no longer changes its size.

       Every .data-table-modal is also a .data-table-container, so one selector covers both; only
       the action-button hiding stays modal-specific (in-dashboard blocks pass
       showActionsInTable:false, so they never create that wrapper at all).

       639.98px (not 640) is Tailwind's max-sm boundary — it stays strictly below the sm:
       min-width:640px rules so the two never both match at exactly 640px. */
    @media (max-width: 639.98px) {
      .data-table-container {
        --data-table-search-icon-width: 20px;
      }
      .data-table-modal .themed-data-table-actions-wrapper {
        @apply !hidden;
      }
      .data-table-container .themed-data-table-search-wrapper {
        @apply !relative !top-auto !right-auto w-full mb-3;
      }
      .data-table-container .themed-data-table-search,
      .data-table-container .themed-data-table-search-collapsed {
        /* Literal 32px (not h-8 → 2rem, which the iframe's 14px root scales to 28px) to match the
           Figma control height exactly. */
        @apply w-full h-[32px] py-0 pl-10 pr-10 bg-card-foreground/5 cursor-text;
      }
      .data-table-container .themed-data-table-search-collapsed::placeholder {
        @apply opacity-100;
      }
      .data-table-container .themed-data-table-search-icon {
        @apply !left-3;
      }
    }
    .themed-dropdown-menu {
      @apply absolute right-0 mt-2 max-w-none w-max min-w-36
        max-h-[calc(100vh-12rem)] overflow-y-auto themed-menu-scrollbar
        shadow-lg rounded-themed
        bg-main-background border border-main-border text-main-foreground;
    }
    .themed-dropdown-item {
      @apply w-full text-left px-4 py-3 gap-2
        text-sm flex items-center transition-colors cursor-pointer duration-150
        text-main-foreground bg-transparent
        hover:bg-main-text-button-hover-background hover:text-main-text-button-hover-foreground;
    }
    .themed-primary-button {
      @apply relative flex shrink-0 items-center px-4 py-2
        rounded-full [corner-shape:squircle] cursor-pointer
        bg-primary-button-background text-primary-button-foreground
        hover:bg-primary-button-hover-background hover:text-primary-button-hover-foreground
        disabled:opacity-50 disabled:cursor-not-allowed;
    }
    .themed-primary-inverted-button {
      @apply relative flex shrink-0 items-center px-4 py-2
        rounded-full [corner-shape:squircle] cursor-pointer
        bg-primary-button-foreground text-primary-button-background
        hover:bg-primary-button-hover-foreground hover:text-primary-button-hover-background
        disabled:opacity-50 disabled:cursor-not-allowed;
    }
    .themed-icon-button {
      @apply w-8 h-8 rounded-full
        flex items-center justify-center cursor-pointer
        bg-main-background border border-main-border
        hover:border-main-muted-foreground
        active:scale-[0.97] active:bg-main-text-button-hover-background active:border-main-muted-foreground
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-button-background;
    }
  }
</style>`;

// Flatpickr theme-aware CSS
export const FLATPICKR_STYLE_TAG = `<style id="flatpickrStyleTag" type="text/css">
  .flatpickr-calendar {
    background: var(--theme--color-main-background);
    border: 1px solid var(--theme--color-card-border);
    border-radius: var(--radius-themed);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -2px rgba(0, 0, 0, 0.2);
    margin-left: 0.8rem;
    margin-top: 0.5rem;
  }
  .flatpickr-calendar.arrowTop::before,
  .flatpickr-calendar.arrowTop::after {
    border-bottom-color: var(--theme--color-card-border);
  }
  .flatpickr-calendar.arrowBottom::before,
  .flatpickr-calendar.arrowBottom::after {
    border-top-color: var(--theme--color-card-border);
  }
  .flatpickr-months { background: transparent; }
  .flatpickr-months .flatpickr-month {
    background: transparent;
    color: var(--theme--color-main-foreground);
    fill: var(--theme--color-main-foreground);
  }
  .flatpickr-current-month { color: var(--theme--color-main-foreground); }
  .flatpickr-current-month .flatpickr-monthDropdown-months {
    background: var(--theme--color-card-background);
    color: var(--theme--color-main-foreground);
  }
  .flatpickr-current-month .flatpickr-monthDropdown-months:hover {
    background: var(--theme--color-main-text-button-hover-background);
  }
  .flatpickr-current-month input.cur-year { color: var(--theme--color-main-foreground); }
  .flatpickr-current-month input.cur-year:focus { background: var(--theme--color-main-text-button-hover-background); }
  .flatpickr-prev-month, .flatpickr-next-month {
    color: var(--theme--color-main-muted-foreground);
    fill: var(--theme--color-main-muted-foreground);
  }
  .flatpickr-prev-month:hover, .flatpickr-next-month:hover {
    color: var(--theme--color-main-foreground);
    fill: var(--theme--color-main-foreground);
  }
  .flatpickr-prev-month:hover svg, .flatpickr-next-month:hover svg { fill: var(--theme--color-primary-button-background); }
  .flatpickr-prev-month svg, .flatpickr-next-month svg { fill: var(--theme--color-main-muted-foreground); }
  .numInputWrapper:hover { background: var(--theme--color-main-text-button-hover-background); }
  .numInputWrapper span { border-color: var(--theme--color-main-border); }
  .numInputWrapper span:hover { background: var(--theme--color-main-text-button-hover-background); }
  .numInputWrapper span::after {
    border-top-color: var(--theme--color-main-muted-foreground);
    border-bottom-color: var(--theme--color-main-muted-foreground);
  }
  .flatpickr-weekdays { background: transparent; }
  span.flatpickr-weekday {
    background: transparent;
    color: var(--theme--color-main-muted-foreground);
    font-weight: 500;
  }
  .flatpickr-days { border: none !important; }
  .flatpickr-innerContainer { border: none !important; }
  .flatpickr-rContainer { border: none !important; }
  .dayContainer { background: transparent; }
  .dayContainer, .dayContainer * { box-shadow: none !important; }
  .flatpickr-day {
    color: var(--theme--color-main-foreground);
    background: transparent;
    border-color: transparent;
  }
  .flatpickr-day:hover, .flatpickr-day:focus {
    background: var(--theme--color-main-text-button-hover-background);
    border-color: var(--theme--color-main-border);
  }
  .flatpickr-day.today { border-color: var(--theme--color-primary-button-background); }
  .flatpickr-day.today:hover, .flatpickr-day.today:focus {
    background: var(--theme--color-main-text-button-hover-background);
    border-color: var(--theme--color-primary-button-background);
    color: var(--theme--color-main-foreground);
  }
  .flatpickr-day.selected,
  .flatpickr-day.startRange,
  .flatpickr-day.endRange,
  .flatpickr-day.selected.inRange,
  .flatpickr-day.startRange.inRange,
  .flatpickr-day.endRange.inRange,
  .flatpickr-day.selected:focus,
  .flatpickr-day.startRange:focus,
  .flatpickr-day.endRange:focus,
  .flatpickr-day.selected:hover,
  .flatpickr-day.startRange:hover,
  .flatpickr-day.endRange:hover,
  .flatpickr-day.selected.prevMonthDay,
  .flatpickr-day.startRange.prevMonthDay,
  .flatpickr-day.endRange.prevMonthDay,
  .flatpickr-day.selected.nextMonthDay,
  .flatpickr-day.startRange.nextMonthDay,
  .flatpickr-day.endRange.nextMonthDay {
    background: var(--theme--color-primary-button-background);
    border-color: var(--theme--color-primary-button-background);
    color: var(--theme--color-primary-button-foreground);
  }
  .flatpickr-day.inRange {
    background: color-mix(in srgb, var(--theme--color-primary-button-background) 15%, transparent);
    border-color: transparent;
    color: var(--theme--color-main-foreground);
  }
  .flatpickr-day.inRange:hover {
    background: color-mix(in srgb, var(--theme--color-primary-button-background) 25%, transparent);
    border-color: var(--theme--color-primary-button-background);
  }
  .flatpickr-day.prevMonthDay, .flatpickr-day.nextMonthDay { color: var(--theme--color-main-muted-foreground); }
  .flatpickr-day.prevMonthDay:hover, .flatpickr-day.nextMonthDay:hover {
    background: var(--theme--color-main-text-button-hover-background);
    border-color: var(--theme--color-main-border);
  }
  .flatpickr-day.prevMonthDay.inRange, .flatpickr-day.nextMonthDay.inRange {
    background: color-mix(in srgb, var(--theme--color-primary-button-background) 10%, transparent);
  }
  .flatpickr-day.flatpickr-disabled, .flatpickr-day.flatpickr-disabled:hover {
    color: var(--theme--color-main-muted-foreground);
    opacity: 0.4;
    background: transparent;
    border-color: transparent;
    cursor: not-allowed;
  }
  .flatpickr-day.flatpickr-disabled.inRange {
    color: var(--theme--color-main-muted-foreground);
    opacity: 0.4;
    background: color-mix(in srgb, var(--theme--color-primary-button-background) 25%, transparent);
    border-color: transparent;
    cursor: not-allowed;
  }
  .flatpickr-time { border-top: 1px solid var(--theme--color-main-border); }
  .flatpickr-time input { color: var(--theme--color-main-foreground); background: transparent; }
  .flatpickr-time input:hover, .flatpickr-time input:focus { background: var(--theme--color-main-text-button-hover-background); }
  .flatpickr-time .flatpickr-time-separator, .flatpickr-time .flatpickr-am-pm { color: var(--theme--color-main-foreground); }
  .flatpickr-time .flatpickr-am-pm:hover, .flatpickr-time .flatpickr-am-pm:focus { background: var(--theme--color-main-text-button-hover-background); }

  /* Flatpickr preset buttons - theme-aware */
  .flatpickr-calendar .flatpickr-presets {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px;
    margin-top: 8px;
    border-top: 1px solid color-mix(in srgb, var(--theme--color-main-border) 75%, transparent);
  }
  .flatpickr-calendar .flatpickr-presets .flexpickr-presets-row {
    display: flex;
    gap: 8px;
    margin: 0;
    padding: 0;
  }
  .flatpickr-calendar .flatpickr-presets button.flatpickr-preset-btn {
    flex: 1;
    padding: 6px 12px;
    margin: 0;
    font-size: 12px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: color-mix(in srgb, var(--theme--color-primary-button-background) 15%, transparent);
    color: var(--theme--color-main-foreground);
    cursor: pointer;
    transition: background-color 0.15s, border-color 0.15s;
  }
  .flatpickr-calendar .flatpickr-presets button.flatpickr-preset-btn:hover {
    background: color-mix(in srgb, var(--theme--color-primary-button-background) 25%, transparent);
    border-color: var(--theme--color-primary-button-background);
  }
</style>`;

// Semantic status utility classes for metric cards. Kept as plain (unlayered) CSS so they win
// over the `text-card-muted-foreground` Tailwind utility applied to the same element — layered
// utilities would otherwise beat rules placed in Tailwind's `@layer components`. The consumed
// `--coeff-*` vars are published per resolved theme by applyTailwindTheme.
export const METRIC_STATUS_STYLE_TAG = `<style id="metricStatusStyleTag" type="text/css">
  .metric-status-positive { color: var(--coeff-positive); }
  .metric-status-negative { color: var(--coeff-negative); }
  .metric-status-neutral { color: var(--coeff-neutral); }
</style>`;

/**
 * Apply Tailwind CSS theme by setting CSS custom properties on the iframe document.
 * These variables are referenced in the Tailwind @theme config.
 *
 * `semanticTokens` (the resolved theme's TEXT tokens — `theme.semanticText`, falling back to the
 * chartjs tokens for legacy themes) are published as the `--coeff-positive/negative/neutral` CSS
 * vars consumed by the `.metric-status-*` utility classes. Falls back to the mode-aware
 * DEFAULT_SEMANTIC_TOKENS when the theme omits them entirely.
 */
export function applyTailwindTheme(
  iframeDocument: Document,
  config: TailwindThemeConfig,
  semanticTokens?: Pick<ChartJSThemeConfig, "positive" | "negative" | "neutral">
): void {
  const root = iframeDocument.documentElement;

  const updateThemeVariable = (name: TailwindThemeVariableName, value: string) => {
    root.style.setProperty(`--theme${name}`, value);
  };

  // General theme variables
  updateThemeVariable("--font-themed", config.general.fontFamily);
  updateThemeVariable("--radius-themed", config.general.borderRadius);

  // Main surface
  updateThemeVariable("--color-main-background", config.main.backgroundColor);
  const mainSecondaryBackground =
    config.main.secondaryBackgroundColor || config.main.textButton.hoverBackgroundColor;
  updateThemeVariable("--color-main-background-secondary", mainSecondaryBackground);
  updateThemeVariable(
    "--color-main-background-tertiary",
    // Fall back to one visible step deeper than secondary: mixing toward the foreground is
    // direction-aware (steps lighter on dark themes) and can only produce zero contrast if
    // foreground equals background, i.e. an unreadable theme.
    config.main.tertiaryBackgroundColor ||
      `color-mix(in srgb, ${mainSecondaryBackground}, ${config.main.foregroundColor} 8%)`
  );
  updateThemeVariable(
    "--color-main-chip-background",
    // Fall back to a light wash of the theme's primary over the main background — approximates
    // the design's blue/100 chip fill for the default palette and stays readable with
    // main-foreground text on any theme.
    config.main.chipBackgroundColor ||
      `color-mix(in srgb, ${config.primaryButton.backgroundColor} 15%, ${config.main.backgroundColor})`
  );
  updateThemeVariable("--color-main-foreground", config.main.foregroundColor);
  updateThemeVariable(
    "--color-main-muted-foreground",
    config.main.mutedForegroundColor || config.main.foregroundColor
  );
  updateThemeVariable("--color-main-border", config.main.borderColor);
  updateThemeVariable("--shadow-main", config.main.shadow);
  updateThemeVariable(
    "--color-main-text-button-hover-background",
    config.main.textButton.hoverBackgroundColor
  );
  updateThemeVariable(
    "--color-main-text-button-hover-foreground",
    config.main.textButton.hoverForegroundColor
  );
  updateThemeVariable(
    "--radius-main-text-button",
    config.main.textButton.borderRadius || config.general.borderRadius
  );

  // Nav surface
  updateThemeVariable("--color-nav-background", config.nav.backgroundColor);
  updateThemeVariable("--color-nav-foreground", config.nav.foregroundColor);
  updateThemeVariable(
    "--color-nav-muted-foreground",
    config.nav.mutedForegroundColor || config.nav.foregroundColor
  );
  updateThemeVariable("--color-nav-border", config.nav.borderColor);
  updateThemeVariable("--shadow-nav", config.nav.shadow);
  updateThemeVariable(
    "--color-nav-text-button-hover-background",
    config.nav.textButton.hoverBackgroundColor
  );
  updateThemeVariable(
    "--color-nav-text-button-hover-foreground",
    config.nav.textButton.hoverForegroundColor
  );
  updateThemeVariable(
    "--radius-nav-text-button",
    config.nav.textButton.borderRadius || config.general.borderRadius
  );

  // Card surface
  updateThemeVariable("--color-card-background", config.card.backgroundColor);
  updateThemeVariable("--color-card-foreground", config.card.foregroundColor);
  updateThemeVariable(
    "--color-card-muted-foreground",
    config.card.mutedForegroundColor || config.card.foregroundColor
  );
  updateThemeVariable("--color-card-border", config.card.borderColor);
  updateThemeVariable("--shadow-card", config.card.shadow);
  updateThemeVariable(
    "--color-card-text-button-hover-background",
    config.card.textButton.hoverBackgroundColor
  );
  updateThemeVariable(
    "--color-card-text-button-hover-foreground",
    config.card.textButton.hoverForegroundColor
  );
  updateThemeVariable(
    "--radius-card-text-button",
    config.card.textButton.borderRadius || config.general.borderRadius
  );

  // AI Image block - use theme-specific or smart defaults based on light/dark
  const aiImageBg =
    config.card.aiImageBackgroundColor || (config.general.isDarkTheme ? "#1a2a3f" : "#DEE9FE");
  updateThemeVariable("--color-ai-image-background", aiImageBg);
  updateThemeVariable(
    "--shadow-ai-image-inset",
    config.card.aiImageInsetShadow || `inset 0px 0px 22px 22px ${aiImageBg}`
  );

  // Primary button
  updateThemeVariable("--color-primary-button-background", config.primaryButton.backgroundColor);
  updateThemeVariable("--color-primary-button-foreground", config.primaryButton.foregroundColor);
  updateThemeVariable(
    "--color-primary-button-hover-background",
    config.primaryButton.hoverBackgroundColor
  );
  updateThemeVariable(
    "--color-primary-button-hover-foreground",
    config.primaryButton.hoverForegroundColor
  );
  updateThemeVariable("--shadow-primary-button", config.primaryButton.shadow);
  updateThemeVariable("--color-primary-button-border", config.primaryButton.borderColor);

  // Secondary button
  updateThemeVariable(
    "--color-secondary-button-background",
    config.secondaryButton.backgroundColor
  );
  updateThemeVariable(
    "--color-secondary-button-foreground",
    config.secondaryButton.foregroundColor
  );
  updateThemeVariable(
    "--color-secondary-button-hover-background",
    config.secondaryButton.hoverBackgroundColor
  );
  updateThemeVariable(
    "--color-secondary-button-hover-foreground",
    config.secondaryButton.hoverForegroundColor
  );
  updateThemeVariable("--shadow-secondary-button", config.secondaryButton.shadow);
  updateThemeVariable("--color-secondary-button-border", config.secondaryButton.borderColor);

  // Theme variables for generating non-Tailwind utility classes
  if (config.card.cardSurfaceBackground) {
    root.style.setProperty("--theme--card-surface-background", config.card.cardSurfaceBackground);
  } else {
    root.style.removeProperty("--theme--card-surface-background");
  }
  root.style.setProperty("--theme--main-border-width", config.main.borderWidth || "0px");
  root.style.setProperty("--theme--nav-border-width", config.nav.borderWidth || "0px");
  root.style.setProperty("--theme--card-border-width", config.card.borderWidth || "0px");
  root.style.setProperty(
    "--theme--page-background",
    config.general.pageBackground || config.main.backgroundColor
  );

  // Text block
  if (config.textBlock) {
    root.style.setProperty("--theme--text-block-background", config.textBlock.background);
    updateThemeVariable(
      "--color-text-block-foreground",
      config.textBlock.foregroundColor || "#ffffff"
    );
    root.style.setProperty("--theme--text-block-border", config.textBlock.border || "none");
  }

  // Dashboard container layer (optional)
  root.style.setProperty(
    "--theme--dashboard-container-background",
    config.dashboardContainer?.background || "transparent"
  );
  root.style.setProperty(
    "--theme--dashboard-container-border-color",
    config.dashboardContainer?.borderColor || "transparent"
  );
  root.style.setProperty(
    "--theme--dashboard-container-border-radius",
    config.dashboardContainer?.borderRadius || config.general.borderRadius
  );
  root.style.setProperty(
    "--theme--dashboard-container-shadow",
    config.dashboardContainer?.shadow || "none"
  );

  // Semantic status tokens (metric cards, etc.). Published as palette-independent CSS vars so
  // non-chart chrome shares one source of truth with charts. Consumed by `.metric-status-*`.
  const semanticDefaults = DEFAULT_SEMANTIC_TOKENS[config.general.isDarkTheme ? "dark" : "light"];
  root.style.setProperty("--coeff-positive", semanticTokens?.positive || semanticDefaults.positive);
  root.style.setProperty("--coeff-negative", semanticTokens?.negative || semanticDefaults.negative);
  root.style.setProperty("--coeff-neutral", semanticTokens?.neutral || semanticDefaults.neutral);
}

/**
 * Apply extra CSS, HTML, and setup function for a theme.
 */
function applyExtraAssets(
  iframeDocument: Document,
  iframeWindow: Window,
  extraCss?: string,
  extraHtml?: string,
  setup?: ThemeSetupFn
): void {
  // Inject extra CSS if provided by the theme
  const extraCssStyleTagId = "extraCssStyleTag";
  let extraCssStyleTag = iframeDocument.getElementById(
    extraCssStyleTagId
  ) as HTMLStyleElement | null;

  if (extraCss) {
    if (!extraCssStyleTag) {
      extraCssStyleTag = iframeDocument.createElement("style");
      extraCssStyleTag.id = extraCssStyleTagId;
      iframeDocument.head.appendChild(extraCssStyleTag);
    }
    extraCssStyleTag.textContent = extraCss;
  } else if (extraCssStyleTag) {
    // Remove the style tag if no extraCss is provided (theme switched)
    extraCssStyleTag.remove();
  }

  // Inject extra HTML if provided by the theme
  const extraHtmlContainerId = "extraHtmlContainer";
  let extraHtmlContainer = iframeDocument.getElementById(
    extraHtmlContainerId
  ) as HTMLDivElement | null;

  if (extraHtml) {
    if (!extraHtmlContainer) {
      extraHtmlContainer = iframeDocument.createElement("div");
      extraHtmlContainer.id = extraHtmlContainerId;
      // Insert at the start of body so it's behind other content
      iframeDocument.body.insertBefore(extraHtmlContainer, iframeDocument.body.firstChild);
    }
    extraHtmlContainer.innerHTML = extraHtml;
  } else if (extraHtmlContainer) {
    // Remove the container if no extraHtml is provided (theme switched)
    extraHtmlContainer.remove();
  }

  // Call previous theme's cleanup before setting up new theme
  if (currentThemeCleanup) {
    currentThemeCleanup();
    currentThemeCleanup = null;
  }

  // Run the theme's setup function if provided
  if (setup) {
    const cleanup = setup(iframeDocument, iframeWindow);
    if (cleanup) {
      currentThemeCleanup = cleanup;
    }
  }
}

/**
 * Apply Chart.js theme by setting global defaults.
 * Also updates existing chart instances.
 */
export function applyChartJSTheme(iframeWindow: DashboardWindow, config: ChartJSThemeConfig): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Chart = iframeWindow.Chart as any;

  if (!Chart?.defaults) {
    return;
  }

  const defaults = Chart.defaults;

  // Set global defaults
  defaults.color = config.defaultColor;
  defaults.backgroundColor = config.defaultBackgroundColor;
  defaults.borderColor = config.defaultBorderColor;

  // Font settings
  if (!defaults.font) {
    defaults.font = {};
  }
  defaults.font.family = config.fontFamily;
  defaults.font.size = config.fontSize;

  // Tooltip styling
  if (!defaults.plugins) {
    defaults.plugins = {};
  }
  if (!defaults.plugins.tooltip) {
    defaults.plugins.tooltip = {};
  }
  defaults.plugins.tooltip.backgroundColor = config.tooltipBackgroundColor;
  defaults.plugins.tooltip.titleColor = config.tooltipTextColor;
  defaults.plugins.tooltip.bodyColor = config.tooltipTextColor;
  defaults.plugins.tooltip.borderColor = config.tooltipBorderColor;
  defaults.plugins.tooltip.borderWidth = 1;

  // Legend styling
  if (!defaults.plugins.legend) {
    defaults.plugins.legend = {};
  }
  if (!defaults.plugins.legend.labels) {
    defaults.plugins.legend.labels = {};
  }
  defaults.plugins.legend.labels.color = config.legendTextColor;
  // Circle swatches everywhere (incl. doughnut/pie, which otherwise draw squares).
  defaults.plugins.legend.labels.usePointStyle = true;
  defaults.plugins.legend.labels.pointStyle = "circle";
  defaults.plugins.legend.labels.boxWidth = 8;
  defaults.plugins.legend.labels.boxHeight = 8;

  // Scale/grid defaults
  if (!defaults.scale) {
    defaults.scale = {};
  }
  if (!defaults.scale.grid) {
    defaults.scale.grid = {};
  }
  defaults.scale.grid.color = config.gridColor;
  if (!defaults.scale.ticks) {
    defaults.scale.ticks = {};
  }
  defaults.scale.ticks.color = config.axisTickColor;

  // Line chart defaults
  if (!defaults.elements) {
    defaults.elements = {};
  }
  if (!defaults.elements.line) {
    defaults.elements.line = {};
  }
  defaults.elements.line.tension = config.lineTension;
  defaults.elements.line.borderWidth = config.lineBorderWidth;

  // Point defaults
  if (!defaults.elements.point) {
    defaults.elements.point = {};
  }
  defaults.elements.point.radius = config.pointRadius;
  defaults.elements.point.hoverRadius = config.pointHoverRadius;
  defaults.elements.point.borderWidth = config.pointBorderWidth;
  // Extra hit radius for better hover/click interaction
  defaults.elements.point.hitRadius = Math.max(0, 8 - config.pointRadius);

  // Bar defaults
  if (!defaults.elements.bar) {
    defaults.elements.bar = {};
  }
  defaults.elements.bar.borderWidth = config.barBorderWidth;
  defaults.elements.bar.borderRadius = config.barBorderRadius;

  // Update all existing chart instances
  if (Chart.instances) {
    Object.values(Chart.instances).forEach((chart: unknown) => {
      try {
        (chart as { update?: () => void })?.update?.();
      } catch {
        // Chart may have been destroyed
      }
    });
  }
}

/**
 * Build AG Grid theme object from config.
 * This returns the theme object to pass to createGrid.
 */
export function buildAGGridTheme(
  iframeWindow: DashboardWindow,
  config: AGGridThemeConfig
): AgGridGlobals.Theme | null {
  const agGrid = iframeWindow.agGrid;

  if (!agGrid) {
    return null;
  }

  // Select base theme
  let baseTheme;
  switch (config.baseTheme) {
    case "quartz":
      baseTheme = agGrid.themeQuartz;
      break;
    case "balham":
      baseTheme = agGrid.themeBalham;
      break;
    case "material":
    default:
      baseTheme = agGrid.themeMaterial;
      break;
  }

  if (!baseTheme) {
    // Fall back to material if the requested theme isn't available
    baseTheme = agGrid.themeMaterial;
  }

  return baseTheme.withParams(config.params);
}

/**
 * Apply all theme settings (Tailwind, Chart.js).
 * AG Grid theme is returned for use when creating grids.
 */
export function applyTheme(iframeWindow: DashboardWindow, theme: Theme): void {
  // Apply Tailwind CSS theme. Use startViewTransition on modern browser versions where it
  // exists for smoother CSS transitions. Note that chart.js has canvas drawing logic which
  // does not work well with view transitions.
  const iframeDocument = iframeWindow.document as {
    startViewTransition?: (callback: () => void) => void;
  };

  if (iframeDocument.startViewTransition) {
    iframeDocument.startViewTransition(() => {
      applyTailwindTheme(
        iframeWindow.document,
        theme.tailwind,
        theme.semanticText ?? theme.chartjs
      );
    });
  } else {
    applyTailwindTheme(iframeWindow.document, theme.tailwind, theme.semanticText ?? theme.chartjs);
  }

  // Apply Chart.js theme
  applyChartJSTheme(iframeWindow, theme.chartjs);

  // Apply extra CSS/HTML assets and run setup function
  applyExtraAssets(
    iframeWindow.document,
    iframeWindow,
    theme.extraCss,
    theme.extraHtml,
    theme.setup
  );

  // Build and store AG Grid theme on window for use by createDataTable
  const agGridTheme = buildAGGridTheme(iframeWindow, theme.agGrid);
  if (agGridTheme) {
    iframeWindow.__coeffAGGridTheme = agGridTheme;
  }
}

/**
 * Get the color palette from the theme for use in chart dataset colors.
 */
export function getChartColorPalette(theme: Theme): string[] {
  return [...theme.chartjs.colorPalette];
}
